/**
 * Cooling System section descriptor (#997 — Story 4.6).
 *
 * The real, live `SectionDescriptor` for the Cooling System dynamic section.
 * It mirrors the Small UPS descriptor (Story 4.5) but binds the ACTUAL
 * Cooling per-unit field set, table, mapper, REST contract, and attachment
 * cards, so the shared descriptor engine can drive Cooling at byte-identical
 * parity with the pre-engine bespoke implementation (Story 4.6 golden masters
 * are the oracle).
 *
 * Field source of truth: `COOLING_READINESS_FIELDS` (`cooling-readiness.fields.ts`)
 * plus the editor slice at `COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS[2..]` —
 * every per-unit field is typed here (text / multilineText / number / yesNo)
 * to match the real controls in `CoolingSystemEditFormContent.tsx`. `name`
 * (user-defined instance name) and `status` (Existing/New) are the engine's
 * identity fields and are handled by the engine list chrome, so they are NOT
 * per-unit descriptor `fields` (they are added back only on the Summary
 * surface via the identity-field label bridge in
 * `coolingSystemDescriptorSummary`).
 *
 * `coolingAdditionalReadinessInfo` is a PER-UNIT "Additional Information"
 * note on `CoolingSystemConfiguration` (migration 108), so it lives on the
 * per-unit fields list and is declared via `additionalInfoField`. Cooling
 * has NO shared request-level field on the descriptor surface (per
 * `COOLING_EDITOR_SHARED_FIELD_KEYS = []` — #997 Bug 9 moved both former
 * shared fields per-instance), so `sharedFieldKeys` is `[]`.
 *
 * Cooling covers the NARROWEST field-type surface among migrated sections in
 * Epic 4: aside from the single `coolingRequiredRedundancyForSystem` dropdown
 * (#997 QA-A restored the governed N/N+1/2N control), the surface is narrow —
 * 2 `text`, 4 `multilineText`, 4 `yesNo`. `coolingTempRisePrefThroughItEquipment` renders as a `NumberInput`
 * in the live editor but is stored as `NVARCHAR(255)` (a STRING on the entity),
 * so #997 Story 6.6 (the live editor swap) types it `text`, not `number`: a
 * `number` type would coerce the stored string `"5"` → number `5` through
 * `descriptorFieldMapToPatch` / `descriptorInstanceToCoolingSystem` and
 * false-dirty every untouched unit (see the field-type table below). The `yesNo`
 * fields split unequally: three are string-backed
 * (`coolingIsExistingSystemOnsite`, `coolingIsChilledWaterAvailable` in
 * `NVARCHAR(255)`) or boolean-backed (`coolingRearDoorHexDesired` in `BIT`) —
 * the descriptor type is control-shape metadata; storage is decided by the
 * field registry and mapper.
 *
 * NFR7: the table (`request_cooling_systems`), mapper
 * (`cooling-system-configuration.mapper`), and permission config are
 * read-only; this descriptor only DECLARES the binding.
 */

import { locationTagDescriptorField } from './sectionDescriptor.types';
import {
  COOLING_SYSTEM_TABLE_COLUMN_ALIGN,
  coolingSystemColumnUsesDoubleLine,
  coolingSystemTableColumnsForLayout,
  coolingSystemTableHeaderLabel,
  formatCoolingSystemTableCell,
  getCoolingSystemColumnSubline,
} from '../../components/coolingsystems/coolingSystemListUtils';
import {
  COOLING_SYSTEM_CONFIGURATIONS_LIST_TITLE,
  COOLING_SYSTEM_DELETE_CONFIRM_MESSAGE,
  COOLING_SYSTEM_DELETE_CONFIRM_TITLE,
  COOLING_SYSTEM_DISCARD_UNSAVED_DISCARD_LABEL,
  COOLING_SYSTEM_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
  COOLING_SYSTEM_DISCARD_UNSAVED_MESSAGE,
  COOLING_SYSTEM_DISCARD_UNSAVED_TITLE,
  COOLING_SYSTEM_EDIT_ALL_UNITS_LABEL,
  COOLING_SYSTEM_EDIT_DELETE_LABEL,
  COOLING_SYSTEM_EDIT_HEADER_TITLE_NEW,
  COOLING_SYSTEM_EDIT_SAVE_DRAFT_LABEL,
  COOLING_SYSTEM_EDIT_SAVE_LABEL,
  COOLING_SYSTEM_EMPTY_STATE_TITLE,
} from '../../constants/coolingSystemConfigurationsSubsectionCopy';
import { REDUNDANCY_OPTIONS } from '../../constants/redundancy';
import { SECTION_IDS } from '../../constants/sectionIds';
import { COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS } from '../fields/requestFieldRegistry';
import { COOLING_READINESS_FIELDS } from '../fields/steps/cooling-readiness.fields';

import type {
  SectionDescriptor,
  SectionDescriptorField,
  SectionDescriptorFieldType,
  SectionDescriptorFieldValidation,
  SectionListColumnsProjection,
} from './sectionDescriptor.types';
import type { CoolingSystemTableColumnKey } from '../../components/coolingsystems/coolingSystemListUtils';
import type { CoolingSystemConfiguration } from '../../types/coolingSystem';

/**
 * Per-field render type + validation, keyed by dataField.
 * Every non-identity per-unit key covered by the descriptor has an entry here;
 * anything missing falls back to `text`. Types match the real Cooling System
 * controls (see `CoolingSystemEditFormContent.tsx`):
 *
 *   - `coolingIsExistingSystemOnsite`         — FormYesNoChoice at :280-289
 *     (string-backed via `value={formData.coolingIsExistingSystemOnsite || ''}`)
 *   - `coolingIsChilledWaterAvailable`        — FormYesNoChoice at :300-311
 *     (string-backed via `value={String(...)}`)
 *   - `coolingTotalAvailableCapacityKw`       — FormInput (text) at :323-334
 *     (label says kW; storage is `NVARCHAR(255)`)
 *   - `coolingDescription`                    — FormInput multiline
 *     `maxLength={10000}` at :260-271 (multilineText)
 *   - `coolingRequiredRedundancyForSystem`    — FormInput (text) at :343-353
 *   - `coolingTempRisePrefThroughItEquipment` — NumberInput at :364-376
 *     (`showStepper={false}`, no min/max, storage is `NVARCHAR(255)`)
 *   - `coolingDescribeApproach`               — FormInput multiline
 *     `maxLength={10000}` at :407-418 (multilineText)
 *   - `coolingRearDoorHexDesired`             — FormYesNoChoice at :385-395
 *     (boolean-backed via `booleanToYesNo`)
 *   - `coolingDescribeRearDoorLoop`           — FormInput multiline
 *     `maxLength={10000}` at :427-438 (multilineText)
 *   - `coolingAdditionalReadinessInfo`        — `RepeatableInstanceAdditionalInfo`
 *     at :444-471 (multilineText, maxLength 10000)
 */
interface CoolingSystemFieldTypeSpec {
  type: SectionDescriptorFieldType;
  options?: readonly string[];
  validation?: SectionDescriptorFieldValidation;
}

const COOLING_SYSTEM_FIELD_TYPES: Readonly<
  Record<string, CoolingSystemFieldTypeSpec>
> = {
  // Rendered by the live editor as a FormYesNoChoice, but both are 'Yes'/'No'
  // STRINGS on the entity (`coolingIsExistingSystemOnsite?: string`,
  // `coolingIsChilledWaterAvailable?: string`) — NOT BIT/booleans like Small UPS's
  // `smallUpsBackupGenerator` or `coolingRearDoorHexDesired`. So they are typed
  // `text` (NOT `yesNo`): the `yesNo` branch of `descriptorFieldMapToPatch` /
  // `descriptorInstanceToCoolingSystem` would coerce the string to a boolean,
  // which (a) crashes the per-unit list formatter's `.trim()`
  // (`coolingSystemListUtils.ts` — "coolingIsExisti.trim is not a function") and
  // (b) breaks the string-backed Yes/No control binding. `text` passes the string
  // through verbatim. (Same trap as the Story 6.5 `powerExistingSiteProtectedByLargeKwUps`
  // and Story 6.6 `coolingTempRisePref` fixes.)
  coolingIsExistingSystemOnsite: { type: 'text' },
  coolingIsChilledWaterAvailable: { type: 'text' },
  coolingTotalAvailableCapacityKw: { type: 'text' },
  coolingDescription: {
    type: 'multilineText',
    validation: { maxLength: 10000 },
  },
  coolingRequiredRedundancyForSystem: {
    type: 'dropdown',
    options: REDUNDANCY_OPTIONS,
    validation: { options: REDUNDANCY_OPTIONS },
  },
  // Rendered by the live editor as a `NumberInput` (no explicit min/max), but the
  // SQL column is `NVARCHAR(255)` and the value is a STRING on
  // `CoolingSystemConfiguration` (`coolingTempRisePrefThroughItEquipment?: string`).
  // So it is typed `text` (NOT `number`): #997 Story 6.6's live swap runs the
  // value through both `descriptorFieldMapToPatch` and
  // `descriptorInstanceToCoolingSystem`, whose `number` branches would coerce the
  // stored string `"5"` → number `5`. The round-tripped session row (`{f:5}`)
  // would then never equal the raw API/mapper baseline row (`{f:"5"}`), and the
  // content-key change detector would flag every untouched unit dirty forever
  // (false-dirty → full PUT on every save). `text` passes the string through
  // verbatim, matching storage. (Analog of the Story 6.5
  // `powerExistingSiteProtectedByLargeKwUps` yesNo→text fix; the bespoke
  // `CoolingSystemEditFormContent` still renders NumberInput regardless of the
  // descriptor type, so the editor UX is unchanged and the Story 4.6 goldens stay
  // byte-identical — the summary value path passes the raw string through.)
  coolingTempRisePrefThroughItEquipment: { type: 'text' },
  coolingDescribeApproach: {
    type: 'multilineText',
    validation: { maxLength: 10000 },
  },
  coolingRearDoorHexDesired: { type: 'yesNo' },
  coolingDescribeRearDoorLoop: {
    type: 'multilineText',
    validation: { maxLength: 10000 },
  },
  // Per-unit "Additional Information" note (migration 108 per-instance column)
  coolingAdditionalReadinessInfo: {
    type: 'multilineText',
    validation: { maxLength: 10000 },
  },
};

/** Registry lookup: dataField → RequestFieldDefinition (for label / subsection). */
const COOLING_READINESS_FIELDS_BY_DATA_FIELD = new Map(
  COOLING_READINESS_FIELDS.map(field => [field.dataField, field]),
);

/** Identity fields the engine list chrome owns; NOT in per-unit `fields`. */
const COOLING_SYSTEM_IDENTITY_KEYS = new Set(['name', 'status']);

/**
 * Per-unit descriptor field dataField keys, in editor order. Sourced from
 * `COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS` minus identity fields (`name`,
 * `status`), plus the note field (`coolingAdditionalReadinessInfo`) appended.
 */
const PER_UNIT_DATA_FIELD_KEYS: readonly string[] = [
  ...COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS.filter(
    field => !COOLING_SYSTEM_IDENTITY_KEYS.has(field.key),
  ).map(field => field.key),
  'coolingAdditionalReadinessInfo',
];

function buildDescriptorField(dataField: string): SectionDescriptorField {
  if (dataField === 'locationTagId') {
    return locationTagDescriptorField('cooling-readiness', 'coolingSystem');
  }
  const registryField = COOLING_READINESS_FIELDS_BY_DATA_FIELD.get(dataField);
  if (!registryField) {
    throw new Error(
      `COOLING_SYSTEM_DESCRIPTOR: no COOLING_READINESS_FIELDS entry for ${dataField}`,
    );
  }
  const spec = COOLING_SYSTEM_FIELD_TYPES[dataField] ?? { type: 'text' };
  return {
    ...registryField,
    type: spec.type,
    options: spec.options,
    validation: spec.validation,
  };
}

/** Typed per-unit descriptor fields (10: 9 per-unit fields + 1 note). */
const COOLING_SYSTEM_DESCRIPTOR_FIELDS: readonly SectionDescriptorField[] =
  PER_UNIT_DATA_FIELD_KEYS.map(buildDescriptorField);

/**
 * Snake_case columns the Cooling System section persists — the exact key
 * set emitted by `cooling-system-configuration.mapper.toEntity`. Migration
 * union: 094 CREATE TABLE (8 per-instance columns) + 108 ALTER TABLE ADD (2
 * per-instance columns) = 10 data columns + 4 identity/audit
 * (`request_id`, `status`, `name`, `display_order`), mirroring the LU 4-3
 * (100+109) and PD 4-4 (103+109) union pattern. The `descriptorDbBinding`
 * guardrail joins this list to both the mapper keys and the migration
 * 094 ∪ 108 column set.
 */
export const COOLING_SYSTEM_DESCRIPTOR_DB_COLUMNS: readonly string[] = [
  'request_id',
  'status',
  'name',
  'location_tag_id',
  'cooling_is_existing_system_onsite',
  'cooling_total_available_capacity_kw',
  'cooling_description',
  'cooling_required_redundancy_for_system',
  'cooling_temp_rise_pref_through_it_equipment',
  'cooling_describe_approach',
  'cooling_rear_door_hex_desired',
  'cooling_describe_rear_door_loop',
  'cooling_is_chilled_water_available',
  'cooling_additional_readiness_info',
  'display_order',
];

/**
 * Cooling System list projection (#997 Story 6.6 — parity by reuse). Delegates to
 * the product-approved bespoke table (`coolingSystemListUtils`: NAME | ΔT
 * | CAPACITY | REDUNDANCY | EXISTING + compact set, sublines, double-line cells).
 * Engine rows carry identity as `instanceName`/`instanceStatus` (stripped from the
 * Cooling shape by `coolingSystemToDescriptorInstance`), so adapt the row back
 * before the Cooling-shaped formatters run — the exact `largeUpsRowForList`
 * pattern.
 */
const coolingSystemRowForList = (
  row: Record<string, unknown>,
): CoolingSystemConfiguration => {
  const unit = row as unknown as CoolingSystemConfiguration;
  return {
    ...unit,
    name: unit.name ?? (row.instanceName as CoolingSystemConfiguration['name']),
    status:
      unit.status ??
      (row.instanceStatus as CoolingSystemConfiguration['status']),
  };
};

const COOLING_SYSTEM_LIST_COLUMNS: SectionListColumnsProjection = {
  locationTagCellColumn: 'ROOM',
  columnsForLayout: coolingSystemTableColumnsForLayout,
  headerLabel: (column, layout) =>
    coolingSystemTableHeaderLabel(
      column as CoolingSystemTableColumnKey,
      layout,
    ),
  formatCell: (row, column) =>
    formatCoolingSystemTableCell(
      coolingSystemRowForList(row),
      column as CoolingSystemTableColumnKey,
    ),
  columnAlign: COOLING_SYSTEM_TABLE_COLUMN_ALIGN,
  getColumnSubline: (row, column, layout) =>
    getCoolingSystemColumnSubline(
      column as CoolingSystemTableColumnKey,
      coolingSystemRowForList(row),
      layout,
    ),
  columnUsesDoubleLine: (column, layout) =>
    coolingSystemColumnUsesDoubleLine(
      column as CoolingSystemTableColumnKey,
      layout,
    ),
};

/** The canonical Cooling System descriptor. */
export const COOLING_SYSTEM_DESCRIPTOR: SectionDescriptor = {
  id: 'cooling-system',
  stepId: 'cooling-readiness',
  labels: {
    entitySingular: 'Cooling System',
    entityPlural: 'Cooling Systems',
    addButtonLabel: 'Add Cooling System',
    emptyStateTitle: COOLING_SYSTEM_EMPTY_STATE_TITLE,
    editFormTitle: COOLING_SYSTEM_EDIT_HEADER_TITLE_NEW,
    editFormSubtitle: COOLING_SYSTEM_CONFIGURATIONS_LIST_TITLE,
    // Chrome copy parity (#997 Story 6.6, mirror Large UPS 6.5 / Small UPS 6.4):
    // bind the bespoke hand-authored copy so the engine chrome is byte-identical
    // to the pre-swap subsection, not template-coincident.
    editSaveLabel: COOLING_SYSTEM_EDIT_SAVE_LABEL,
    editSaveDraftLabel: COOLING_SYSTEM_EDIT_SAVE_DRAFT_LABEL,
    editDeleteLabel: COOLING_SYSTEM_EDIT_DELETE_LABEL,
    deleteConfirmTitle: COOLING_SYSTEM_DELETE_CONFIRM_TITLE,
    deleteConfirmMessage: COOLING_SYSTEM_DELETE_CONFIRM_MESSAGE,
    editAllListLabel: COOLING_SYSTEM_EDIT_ALL_UNITS_LABEL,
    editDiscardTitle: COOLING_SYSTEM_DISCARD_UNSAVED_TITLE,
    editDiscardMessage: COOLING_SYSTEM_DISCARD_UNSAVED_MESSAGE,
    editDiscardConfirmLabel: COOLING_SYSTEM_DISCARD_UNSAVED_DISCARD_LABEL,
    editDiscardKeepEditingLabel:
      COOLING_SYSTEM_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
    newInstanceChromeTitle: COOLING_SYSTEM_EDIT_HEADER_TITLE_NEW,
  },
  cardinality: 'many',
  fields: COOLING_SYSTEM_DESCRIPTOR_FIELDS,
  permissionRef: {
    rbacAnchorDataField: 'coolingIsExistingSystemOnsite',
  },
  summaryOrder: COOLING_SYSTEM_DESCRIPTOR_FIELDS.map(field => field.dataField),
  pdfLayout: {
    stepId: 'cooling-readiness',
    anchor: 'Cooling System Information',
    perUnitFieldGroupKeys: ['coolingSystemInfoFields'],
  },
  // Cooling has NO request-level shared fields — per registry
  // `COOLING_EDITOR_SHARED_FIELD_KEYS = []`. #997 Bug 9 moved both former
  // shared fields (`coolingIsChilledWaterAvailable`,
  // `coolingAdditionalReadinessInfo`) to per-instance columns in migration 108.
  sharedFieldKeys: [],
  // The live Cooling editor renders TWO per-unit attachment carousels: the
  // details card at `SECTION_IDS.COOLING_READINESS_ADVANCED`
  // (`CoolingSystemEditFormContent.tsx:161-186` via
  // `resolveRepeatableInstanceAttachmentSectionId`) AND the per-unit
  // Additional Information card at `SECTION_IDS.COOLING_READINESS_ADDITIONAL`
  // (`CoolingSystemEditFormContent.tsx:444-471` via the additional-section id
  // resolver). Both are declared here so the descriptor stays truthful for the
  // deferred live-editor swap follow-up. Ordering matters:
  // `DescriptorInstanceEditFormBody` derives the per-unit Additional Info
  // section id from `attachmentBaseSectionIds[length - 1]`, so
  // `COOLING_READINESS_ADDITIONAL` MUST be last (the `_ADDITIONAL`-last
  // convention: the per-unit Additional-Info card is scoped through this base,
  // and the descriptor engine derives the additional section id from the final
  // entry). Kept in lockstep with the paired two-entry
  // `COOLING_SYSTEM_REPEATABLE_ATTACHMENT_BASE_SECTIONS` constant
  // (`repeatableInstanceAttachmentSections.ts`); a per-section equality guard
  // asserts the two lists match (#997 B3).
  attachmentsEnabled: true,
  attachmentBaseSectionIds: [
    SECTION_IDS.COOLING_READINESS_ADVANCED,
    SECTION_IDS.COOLING_READINESS_ADDITIONAL,
  ],
  additionalInfoField: 'coolingAdditionalReadinessInfo',
  listColumns: COOLING_SYSTEM_LIST_COLUMNS,
  offlineBinding: {
    restResource: 'cooling-systems',
    // Cooling System is the FOURTH section wired onto the engine save/flush path
    // (#997 Story 6.6). It opts in explicitly via the delegating flush shim
    // `flushCoolingSystemDescriptorEditSession` (see `resolveDelegatedFlushBinding`).
    delegatedFlush: true,
    reorderIdsBodyKey: 'coolingSystemIds',
    persistedInstancesKey: 'coolingSystemConfigurations',
    instanceIdOpField: 'coolingSystemId',
    reorderIdsOpField: 'coolingSystemIds',
  },
  dbBinding: {
    table: 'request_cooling_systems',
    columns: COOLING_SYSTEM_DESCRIPTOR_DB_COLUMNS,
  },
};
